import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  Maximize2,
  Minimize2,
  Music2,
  VolumeX,
  Printer,
  Home as HomeIcon,
} from "lucide-react";
import "@/App.css";
import { wonders } from "@/data/wonders";
import { SkyDecorations } from "@/components/SkyDecorations";
import { TitleSlide } from "@/components/TitleSlide";
import { WonderSlide } from "@/components/WonderSlide";
import { FinalQuizSlide } from "@/components/FinalQuizSlide";
import { FinalSlide } from "@/components/FinalSlide";
import { YzaBot } from "@/components/YzaBot";

// Slide types: 0 = title, 1..7 = wonders, 8 = final quiz, 9 = thank you
const TOTAL_SLIDES = 1 + wonders.length + 1 + 1;

const slideLabel = (i) => {
  if (i === 0) return "Welcome";
  if (i >= 1 && i <= wonders.length) return wonders[i - 1].name;
  if (i === wonders.length + 1) return "Quiz Time";
  return "Thank You";
};

const playClick = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "triangle";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.08, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    o.start();
    o.stop(ctx.currentTime + 0.2);
  } catch (_) { /* no-op */ }
};

function App() {
  const [slide, setSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [dark, setDark] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [music, setMusic] = useState(false);
  const audioRef = useRef(null);
  const touchStart = useRef(null);

  const goTo = useCallback(
    (target) => {
      if (target < 0 || target >= TOTAL_SLIDES) return;
      setDirection(target > slide ? 1 : -1);
      setSlide(target);
      playClick();
    },
    [slide]
  );

  const next = useCallback(() => {
    if (slide < TOTAL_SLIDES - 1) goTo(slide + 1);
  }, [slide, goTo]);

  const prev = useCallback(() => {
    if (slide > 0) goTo(slide - 1);
  }, [slide, goTo]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "Home") { goTo(0); }
      else if (e.key === "End") { goTo(TOTAL_SLIDES - 1); }
      else if (e.key === "f" || e.key === "F") { toggleFullscreen(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, prev, goTo]);

  // Touch swipe
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
    touchStart.current = null;
  };

  // Dark mode
  useEffect(() => {
    document.body.classList.toggle("dark", dark);
  }, [dark]);

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);
  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // Background music (opt-in)
  useEffect(() => {
    if (!audioRef.current) return;
    if (music) {
      audioRef.current.volume = 0.25;
      audioRef.current.play().catch(() => setMusic(false));
    } else {
      audioRef.current.pause();
    }
  }, [music]);

  const handlePrint = () => window.print();

  const progress = ((slide + 1) / TOTAL_SLIDES) * 100;

  // Slide renderer
  const renderSlide = (i) => {
    if (i === 0) return <TitleSlide onStart={next} />;
    if (i >= 1 && i <= wonders.length) {
      return <WonderSlide wonder={wonders[i - 1]} index={i} total={wonders.length} />;
    }
    if (i === wonders.length + 1) return <FinalQuizSlide />;
    return <FinalSlide />;
  };

  return (
    <div className="App" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="slide-stage sky-bg" data-testid="presentation-stage">
        <SkyDecorations />

        {/* Progress bar */}
        <div className="progress-track" aria-hidden="true">
          <div className="progress-fill" style={{ width: `${progress}%` }} data-testid="progress-fill" />
        </div>

        {/* Top controls */}
        <div className="top-controls">
          <button
            className="icon-btn"
            onClick={() => goTo(0)}
            title="Home"
            aria-label="Back to title"
            data-testid="home-btn"
          >
            <HomeIcon className="w-5 h-5" />
          </button>
          <YzaBot />
          <button
            className="icon-btn"
            onClick={() => setMusic((m) => !m)}
            title={music ? "Mute music" : "Play music"}
            aria-label="Toggle music"
            data-testid="music-toggle-btn"
          >
            {music ? <Music2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <button
            className="icon-btn"
            onClick={() => setDark((d) => !d)}
            title={dark ? "Light mode" : "Dark mode"}
            aria-label="Toggle dark mode"
            data-testid="dark-mode-btn"
          >
            {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button
            className="icon-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            aria-label="Toggle fullscreen"
            data-testid="fullscreen-btn"
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
          <button
            className="icon-btn"
            onClick={handlePrint}
            title="Print / Save as PDF"
            aria-label="Print"
            data-testid="print-btn"
          >
            <Printer className="w-5 h-5" />
          </button>
        </div>

        {/* Slide transitions */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={slide}
            custom={direction}
            initial={{ opacity: 0, x: direction * 80 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -80 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: "absolute", inset: 0 }}
            data-testid={`slide-${slide}`}
          >
            {renderSlide(slide)}
          </motion.div>
        </AnimatePresence>

        {/* Bottom nav */}
        {slide > 0 && (
          <div className="nav-bar" data-testid="nav-bar">
            <button
              className="nav-btn"
              onClick={prev}
              disabled={slide === 0}
              data-testid="prev-btn"
            >
              <ChevronLeft className="w-5 h-5" /> <span className="nav-label">Previous</span>
            </button>

            <div className="flex items-center gap-3">
              <div className="slide-indicator" data-testid="slide-indicator">
                Slide {slide + 1} of {TOTAL_SLIDES}
                <span className="hidden-on-mobile hidden sm:inline text-slate-400"> · {slideLabel(slide)}</span>
              </div>
            </div>

            <button
              className="nav-btn primary"
              onClick={next}
              disabled={slide === TOTAL_SLIDES - 1}
              data-testid="next-btn"
            >
              <span className="nav-label">Next</span> <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Optional bg music – plays only when toggled. Local file, works offline. */}
        <audio
          ref={audioRef}
          src={`${process.env.PUBLIC_URL || ""}/music.mp3`}
          loop
          preload="none"
        />
      </div>
    </div>
  );
}

export default App;
